import { jwtDecode } from "jwt-decode";

import { type User, authStore } from "@/stores/auth/auth-store";

import { AuthApiAdapter } from "../api/auth-api-adapter";
import { getPermissionsForRoles } from "./permissions-registry";

interface JwtPayload {
  exp: number;
}

// Global refresh state to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

/**
 * Perform login against the API and store the received tokens.
 * A minimal user object is created for now since backend user details
 * are not yet available.
 */
export async function login(email: string, password: string): Promise<User> {
  const authResponse = await AuthApiAdapter.login(email, password);
  const { token, refresh_token: refreshToken } = authResponse;

  if (!token || !refreshToken) {
    throw new Error("Failed to obtain authentication tokens");
  }

  const expiresAt = new Date(jwtDecode<JwtPayload>(token).exp * 1000);

  // Backend returns plain string refresh token (not JWT), so set a default expiration (30 days)
  const refreshTokenExpiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const user: User = {
    id: "", // Will be populated by syncUserData
    email,
    username: email,
    token,
    refreshToken,
    expiresAt,
    refreshTokenExpiresAt,
    permissions: [],
    roles: [],
  };

  authStore.getState().setUser(user);
  return user;
}

/**
 * Refresh the access token using the current refresh token and update the store.
 * Prevents concurrent refresh attempts and infinite loops.
 */
export async function refresh(): Promise<void> {
  // If already refreshing, wait for the current refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const { user, setUser } = authStore.getState();

  // Check if refresh token is available and not expired
  const refreshToken = user?.refreshToken;
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  if (authStore.getState().isRefreshTokenExpired()) {
    throw new Error("Refresh token has expired");
  }

  // Set refreshing state and create promise
  isRefreshing = true;
  refreshPromise = performRefresh(refreshToken, user, setUser);

  try {
    await refreshPromise;
  } finally {
    // Reset refreshing state
    isRefreshing = false;
    refreshPromise = null;
  }
}

/**
 * Internal function to perform the actual refresh operation
 */
async function performRefresh(refreshToken: string, user: User, setUser: (user: User) => void): Promise<void> {
  console.log("Refreshing authentication tokens...");

  const authResponse = await AuthApiAdapter.refresh(refreshToken);
  const { token, refresh_token: newRefreshToken } = authResponse;

  if (!token || !newRefreshToken) {
    throw new Error("Failed to refresh authentication tokens");
  }

  const expiresAt = new Date(jwtDecode<JwtPayload>(token).exp * 1000);

  // Backend returns plain string refresh token (not JWT), so set a default expiration (30 days)
  const refreshTokenExpiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  setUser({
    ...user,
    token,
    refreshToken: newRefreshToken,
    expiresAt,
    refreshTokenExpiresAt,
  });
}

/**
 * Fetch user data from the backend /me endpoint and update the auth store
 * with proper permissions and roles
 */
export async function syncUserData(): Promise<User | null> {
  const { user, setUser } = authStore.getState();

  if (!user) {
    return null;
  }

  try {
    const userData = await AuthApiAdapter.me();

    const updatedUser: User = {
      ...user,
      id: userData.id || user.id,
      email: userData.email || user.email,
      username: userData.email || user.username,
      roles: userData.roles || [],
      permissions: getPermissionsForRoles(userData.roles || []),
    };

    setUser(updatedUser);
    return updatedUser;
  } catch (error) {
    console.error("Failed to sync user data:", error);
    return user; // Don't break auth if sync fails
  }
}

/**
 * Enhanced login function that also syncs user data from backend
 */
export async function loginWithUserData(email: string, password: string): Promise<User> {
  // First perform basic login to get tokens
  const user = await login(email, password);

  // Then sync user data from backend
  const syncedUser = await syncUserData();

  return syncedUser || user;
}

/**
 * Initialize user permissions after authentication
 * This should be called when the app starts and user is already authenticated
 */
export async function initializeUserPermissions(): Promise<void> {
  const { user } = authStore.getState();

  if (user?.token) {
    try {
      await syncUserData();
    } catch (error) {
      console.warn("Failed to initialize user permissions:", error);
      // Continue with minimal user data - don't break the app
    }
  }
}

/**
 * Clear authentication information.
 */
export function logout(): void {
  authStore.getState().clearAuth();
}
